import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet as WalletIcon, Plus, TrendingUp, ArrowDownLeft, ArrowUpRight, IndianRupee, ShieldCheck, X } from 'lucide-react';
import DesktopHeader from '../components/DesktopHeader';
import BottomNav from '../components/BottomNav';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const AMOUNTS = [
  { id: 'wallet_100', amount: 100 },
  { id: 'wallet_500', amount: 500 },
  { id: 'wallet_1000', amount: 1000 },
  { id: 'wallet_2000', amount: 2000 },
  { id: 'wallet_5000', amount: 5000 },
];

const Wallet = () => {
  const nav = useNavigate();
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState('wallet_500');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet`, authCfg());
      setBalance(data.balance);
      setTxns(data.transactions || []);
    } catch (e) {
      if (e?.response?.status === 401) nav('/login');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const topup = async () => {
    if (!selectedPkg) { setMsg({ ok: false, text: 'Choose a top-up package' }); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        purpose: 'wallet_topup',
        package_id: selectedPkg,
        origin_url: window.location.origin,
      }, authCfg());
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setMsg({ ok: false, text: 'Could not start Stripe checkout' });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Top-up failed' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="max-w-[900px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <button onClick={() => nav(-1)} className="md:hidden mb-3 inline-flex items-center gap-1 text-sm text-neutral-600"><ArrowLeft className="w-4 h-4" /> Back</button>

        {/* Balance card */}
        <div data-testid="wallet-balance-card" className="bg-white border border-neutral-200 rounded-2xl p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
              <WalletIcon className="w-6 h-6 text-[#2E7D32]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Clean24 Wallet</p>
              <p className="mt-1 text-3xl md:text-4xl font-extrabold text-neutral-900 flex items-center" data-testid="wallet-balance-value">
                <IndianRupee className="w-6 h-6 md:w-7 md:h-7 text-neutral-800" />
                {balance.toLocaleString('en-IN')}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500">Use at checkout · Cash/UPI top-up</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="wallet-open-topup-btn" onClick={() => setShowTopup(true)} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Add money
            </button>
            <button onClick={() => nav('/rewards')} className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#2E7D32] font-semibold text-sm px-4 py-2 rounded-lg transition-colors text-neutral-700">
              <TrendingUp className="w-4 h-4" /> View rewards
            </button>
          </div>
        </div>

        {msg && (
          <div data-testid="wallet-msg" className={`mt-3 text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}>
            {msg.text}
          </div>
        )}

        {/* Transactions */}
        <div className="mt-6 bg-white border border-neutral-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
            <p className="font-bold text-sm">Recent transactions</p>
            <span className="text-xs text-neutral-500">{txns.length} entries</span>
          </div>
          {loading ? (
            <p className="p-6 text-center text-sm text-neutral-500">Loading…</p>
          ) : txns.length === 0 ? (
            <div className="p-8 text-center">
              <WalletIcon className="w-8 h-8 text-neutral-300 mx-auto" />
              <p className="mt-2 text-sm text-neutral-500">No transactions yet</p>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100" data-testid="wallet-txn-list">
              {txns.map((t) => (
                <li key={t.id} className="px-4 py-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${t.type === 'credit' ? 'bg-[#E8F5E9]' : 'bg-[#FEF3C7]'}`}>
                    {t.type === 'credit'
                      ? <ArrowDownLeft className="w-4 h-4 text-[#2E7D32]" />
                      : <ArrowUpRight className="w-4 h-4 text-[#D97706]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{t.note || (t.type === 'credit' ? 'Wallet credit' : 'Wallet debit')}</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5">{new Date(t.created_at).toLocaleString('en-IN')} · {t.source}</p>
                  </div>
                  <span className={`font-bold text-sm ${t.type === 'credit' ? 'text-[#2E7D32]' : 'text-[#D97706]'}`}>
                    {t.type === 'credit' ? '+' : '−'} ₹{t.amount}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-3 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> Payments powered by Stripe (test mode). Balance credited automatically once payment succeeds.</p>
      </div>

      {/* Topup modal */}
      {showTopup && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={() => setShowTopup(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-2xl p-5" data-testid="wallet-topup-modal">
            <div className="flex items-center justify-between">
              <p className="font-extrabold text-lg">Add money</p>
              <button onClick={() => setShowTopup(false)} className="text-neutral-500"><X className="w-5 h-5" /></button>
            </div>
            <p className="mt-1 text-sm text-neutral-500">Pick a top-up amount. You'll be redirected to Stripe (test mode).</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {AMOUNTS.map((a) => (
                <button key={a.id} onClick={() => setSelectedPkg(a.id)} data-testid={`wallet-quick-${a.amount}`}
                  className={`py-3 rounded-lg border text-sm font-semibold transition-colors ${selectedPkg === a.id ? 'bg-[#2E7D32] border-[#2E7D32] text-white' : 'bg-white border-neutral-200 text-neutral-700 hover:border-[#2E7D32]'}`}>
                  ₹{a.amount.toLocaleString('en-IN')}
                </button>
              ))}
            </div>
            <div className="mt-4 rounded-lg bg-[#F0F9F1] border border-[#C8E6C9] p-3 text-xs text-[#1B5E20]">
              Test card: <span className="font-mono font-bold">4242 4242 4242 4242</span> · Any future date · Any CVC
            </div>
            <button data-testid="wallet-topup-submit-btn" onClick={topup} disabled={busy || !selectedPkg} className="mt-4 w-full py-3 rounded-xl bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-bold">
              {busy ? 'Redirecting…' : 'Pay with Stripe'}
            </button>
            <p className="mt-2 text-[10px] text-center text-neutral-400">Wallet is credited only after Stripe confirms payment.</p>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Wallet;
